import { useEffect, useState, useCallback } from "react";
import { Users, Star, TrendingUp, Coins, Cake, Crown, ArrowUpRight } from "lucide-react";
import api from "../../lib/api";

const formatEUR = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

/**
 * Lightweight summary card shown at the top of the Customers tab. Acts as the
 * de-facto homepage for Starter-plan users (who don't see the full Dashboard).
 *
 * KPIs:
 *  • Clientes activos (last 30d)
 *  • Nuevos este mes
 *  • Puntos canjeados este mes
 *  • Próximos cumpleaños (next 30d)
 *  • Top fidelizados (top 5 by points)
 */
export function LoyaltyMiniDashboard({ restaurantId, onJumpToCustomer }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const r = await api.getLoyaltyMiniDashboard(restaurantId);
      setData(r);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="h-28 rounded-2xl bg-muted/40 animate-pulse" />
        ))}
      </div>
    );
  }
  if (!data) return null;

  const kpis = [
    {
      icon: Users,
      label: "Clientes",
      value: data.total_customers.toLocaleString("es-ES"),
      hint: data.total_customers
        ? `${data.active_customers} activos · ${data.new_customers_month} nuevos este mes`
        : "Comparte tu QR para empezar a captar",
      tint: "text-emerald-500 bg-emerald-500/10",
    },
    {
      icon: Star,
      label: "Puntos activos",
      value: data.total_points_active.toLocaleString("es-ES"),
      hint: "Acumulados por todos los clientes",
      tint: "text-amber-500 bg-amber-500/10",
    },
    {
      icon: TrendingUp,
      label: "Total gastado",
      value: formatEUR(data.total_spent_lifetime),
      hint: "Histórico del programa",
      tint: "text-sky-500 bg-sky-500/10",
    },
    {
      icon: Coins,
      label: "Canjeados este mes",
      value: data.points_redeemed_month.toLocaleString("es-ES"),
      hint: `${data.redemptions_count_month} canje${data.redemptions_count_month === 1 ? "" : "s"} · pts usados`,
      tint: "text-fuchsia-500 bg-fuchsia-500/10",
    },
  ];

  return (
    <div className="space-y-6 mb-6" data-testid="loyalty-mini-dashboard">
      {/* KPIs row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <div
            key={k.label}
            className="rounded-2xl border border-border/60 bg-card/80 backdrop-blur-sm p-5 hover:shadow-lg transition-shadow"
            data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <div className="flex items-start justify-between">
              <div className={`w-10 h-10 rounded-xl ${k.tint} flex items-center justify-center`}>
                <k.icon className="w-5 h-5" strokeWidth={2} />
              </div>
            </div>
            <p className="text-3xl font-semibold mt-3 tracking-tight">{k.value}</p>
            <p className="text-sm text-muted-foreground font-medium mt-0.5">{k.label}</p>
            <p className="text-xs text-muted-foreground/80 mt-1">{k.hint}</p>
          </div>
        ))}
      </div>

      {/* Two-column lists */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Upcoming birthdays */}
        <div className="rounded-2xl border border-border/60 bg-card/80 backdrop-blur-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-pink-500/10 text-pink-500 flex items-center justify-center">
              <Cake className="w-4 h-4" />
            </div>
            <h3 className="font-semibold">Próximos cumpleaños</h3>
            <span className="text-xs text-muted-foreground ml-auto">Próximos 30 días</span>
          </div>
          {data.upcoming_birthdays.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No hay cumpleaños próximos.<br />
              <span className="text-xs">Pide la fecha al registrar nuevos clientes para enviarles ofertas el día de su aniversario.</span>
            </p>
          ) : (
            <ul className="divide-y divide-border/40">
              {data.upcoming_birthdays.map((b) => (
                <li
                  key={b.customer_id}
                  onClick={() => onJumpToCustomer?.(b.customer_id)}
                  className="flex items-center justify-between py-2.5 cursor-pointer hover:bg-muted/30 -mx-2 px-2 rounded-lg transition-colors"
                  data-testid={`birthday-${b.customer_id}`}
                >
                  <div>
                    <p className="text-sm font-medium">{b.name || "Sin nombre"}</p>
                    <p className="text-xs text-muted-foreground">{b.email || "—"}</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    b.days_until === 0
                      ? "bg-pink-500 text-white"
                      : b.days_until <= 3
                        ? "bg-pink-500/15 text-pink-600 dark:text-pink-400"
                        : "bg-muted text-muted-foreground"
                  }`}>
                    {b.days_until === 0 ? "¡Hoy!" : `en ${b.days_until}d`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top customers */}
        <div className="rounded-2xl border border-border/60 bg-card/80 backdrop-blur-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center">
              <Crown className="w-4 h-4" />
            </div>
            <h3 className="font-semibold">Top fidelizados</h3>
            <span className="text-xs text-muted-foreground ml-auto">Por puntos</span>
          </div>
          {data.top_customers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Aún no hay clientes con puntos.<br />
              <span className="text-xs">Lánzales el primer canje desde la pestaña Programa.</span>
            </p>
          ) : (
            <ul className="divide-y divide-border/40">
              {data.top_customers.map((c, i) => (
                <li
                  key={c.customer_id}
                  onClick={() => onJumpToCustomer?.(c.customer_id)}
                  className="flex items-center justify-between py-2.5 cursor-pointer hover:bg-muted/30 -mx-2 px-2 rounded-lg transition-colors group"
                  data-testid={`top-customer-${c.customer_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`text-sm font-bold w-6 text-center flex-shrink-0 ${
                      i === 0 ? "text-amber-500" : i === 1 ? "text-slate-400" : i === 2 ? "text-orange-500" : "text-muted-foreground"
                    }`}>
                      #{i + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.level || c.email || "—"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-amber-500 font-semibold text-sm flex-shrink-0">
                    {c.points.toLocaleString("es-ES")}
                    <span className="text-xs text-muted-foreground font-normal">pts</span>
                    <ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground/0 group-hover:text-muted-foreground transition-colors" />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
